import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
} from '../styles';
import { Button, Container, Overline } from './ui';

const StyledBanner = styled.section`
  position: relative;

  ${({ hasBackgroundImage }) => {
    if (hasBackgroundImage) {
      return css`
        ${minBreakpointQuery.large`
          ${sectionPaddings()}
        `}
      `;
    }
  }}
`;

const StyledInner = styled.div`
  margin: 40px 0;

  ${minBreakpointQuery.small`
    margin-top: 50px;
    margin-bottom: 50px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 60px;
    margin-bottom: 60px;
  `}
    
  ${({ isDetail }) => {
    if (isDetail) {
      return css`
        ${minBreakpointQuery.large`
          margin: 0;
        `}
      `;
    }
  }}
`;

const StyledContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 590px;
`;

const StyledOverline = styled(Overline)`
  margin-bottom: 20px;

  ${minBreakpointQuery.small`
    margin-bottom: 24px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 28px;
  `}
`;

const StyledHeading = styled.h1`
  color: ${brandColours.quaternary};
  font-weight: ${fontWeights.regular};
  ${fluidFontSize(
    '28px',
    '55px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  line-height: 1.2;
`;

const StyledButton = styled(Button)`
  margin-top: 20px;

  ${maxBreakpointQuery.small`
    width: 100%;
  `}

  ${minBreakpointQuery.small`
    margin-top: 24px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 28px;
  `}
`;

const StyledText = styled.p`
  color: ${brandColours.primary};
  margin-top: 20px;

  ${minBreakpointQuery.small`
    ${fontSize(18)};
    margin-top: 24px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 28px;
  `}
`;

const StyledBackgroundImage = styled(GatsbyImage)`
  ${maxBreakpointQuery.large`
    max-height: 50vw;
  `}

  ${minBreakpointQuery.large`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    max-width: 60%;
  `}

  &:after {
    ${minBreakpointQuery.large`
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: linear-gradient(
        to right,
        ${standardColours.white},
        ${standardColours.transparent} 40%
      );
    `}
  }
`;

const Banner = ({
  overline,
  heading,
  text,
  time,
  link,
  backgroundImage,
  isDetail,
}) => (
  <StyledBanner hasBackgroundImage={backgroundImage}>
    <Container>
      <StyledInner isDetail={isDetail}>
        <StyledContent>
          {overline && <StyledOverline>{overline}</StyledOverline>}
          <StyledHeading>{heading}</StyledHeading>
          {link && <StyledButton to={link.pageUrl}>{link.text}</StyledButton>}
          {(text || time) && (
            <StyledText>
              {time ? (
                <time datetime={time.fullDate}>{time.displayDate}</time>
              ) : (
                <span>{text}</span>
              )}
            </StyledText>
          )}
        </StyledContent>
      </StyledInner>
    </Container>
    {backgroundImage && (
      <StyledBackgroundImage
        image={backgroundImage.gatsbyImageData}
        alt={backgroundImage.alt}
      />
    )}
  </StyledBanner>
);

export default Banner;
